import { httpsCallable } from "firebase/functions";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db, functions, ensureAuthenticated } from "../firebase";

/**
 * Resolve a sign code to its public state.
 *
 * The `signs` collection is super-admin-only for reads, so this goes through a
 * callable rather than a direct Firestore read.
 *
 * Resolves to `{ status, code, slug }` where status is one of
 * 'unknown' | 'unactivated' | 'activated' | 'void'. `slug` is only present for
 * activated signs.
 */
export async function lookupSignCode(code) {
  await ensureAuthenticated();
  const res = await httpsCallable(functions, "lookupSign")({ code });
  return res.data;
}

/** True if a memorial exists at this slug. */
export async function memorialSlugExists(slug) {
  const snap = await getDocs(
    query(collection(db, "memorials"), where("slug", "==", slug), limit(1))
  );
  return !snap.empty;
}

/**
 * Pull a memorial slug out of anything a visitor might paste — a full URL, a
 * bare path, or the slug on its own. Returns null if the input doesn't look
 * like a memorial reference at all.
 */
export function parseMemorialSlug(input) {
  const value = (input || "").trim();
  if (!value) return null;

  if (/^https?:\/\//i.test(value)) {
    try {
      const match = new URL(value).pathname.match(/\/m\/([^/]+)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  }

  if (value.startsWith("/m/")) return value.replace("/m/", "").split("/")[0] || null;
  if (value.startsWith("/")) return value.slice(1).split("/")[0] || null;

  return value;
}
